import { Box } from '@mui/system';
import { useForm, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from 'react-query';
import SuspenseLoader from 'src/components/SuspenseLoader';
import createAlbum from 'src/hooks/query/CRM/groups/add-group';
import UpdateGroup from 'src/hooks/query/CRM/groups/update-group';
import useGroup from 'src/hooks/query/CRM/groups/use-group';
import { groupRequest } from 'src/models/CRM';
import useCustomers from 'src/hooks/query/CRM/customers/use-customers';
import { fetchStudents } from 'src/services/students';
import FilepondUploader from 'src/components/filepond-uploader';
import AddUsers from 'src/content/CRM/groups/create-group/add-users';
import { albumEditRequest, albumRequest } from 'src/models/gallery';
import useCreateAlbum from 'src/hooks/query/gallery/useCreateAlbum';
import useGetALbum from 'src/hooks/query/gallery/useGetAlbum';
import useUpdateAlbum from 'src/hooks/query/gallery/useUpdateAlbum';

const inputStyle = {
  width: { xs: '100%', sm: '48%' },
  marginBottom: '30px',
  marginLeft: { sm: '16px' }
};
const AddAlbumForm = ({ setOpen, id }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<albumRequest>({
    defaultValues: {}
  });

  const queryClient = useQueryClient();

  const createAlbum = useCreateAlbum();

  const updateAlbum = useUpdateAlbum(id);

  const { data: albumData, isLoading, isFetching } = useGetALbum(id);

  const {
    data: students,
    isLoading: loadingStudents,
    isError,
    isFetching: fetchingStudents
  } = useQuery(['students'], () => fetchStudents());

  const onSubmit = (data: albumRequest) => {
    if (data?.users?.length == 0) delete data.users;
    if (data?.image?.length == 0) delete data.image;
    //@ts-ignore
    const modefiedData = { ...data, users: data?.users?.map((u) => u.id) };
    createAlbum
      .mutateAsync(modefiedData)
      .then((res) => {
        if (res.status === 201 || res.status === 202) {
          toast.success('Album Added Successfuly');
          queryClient.invalidateQueries('albums');
          setOpen(false);
        }
      })
      .catch((error) => {
        toast.error('Something went wrong, please try again later.');
      });
  };

  const onUpdate = (data: albumEditRequest) => {
    if (data?.users?.length == 0) delete data.users;
    //@ts-ignore
    const modefiedData = { ...data, users: data?.users?.map((u) => u.id) };
    updateAlbum
      .mutateAsync(modefiedData)
      .then((res) => {
        if (res.status === 201 || res.status === 202) {
          toast.success('Album Edited Successfuly');
          queryClient.invalidateQueries('albums');
          setOpen(false);
        }
      })
      .catch((error) => {
        toast.error('Something went wrong, please try again later.');
      });
  };

  useEffect(() => {
    if (albumData && id !== 0) {
      reset({
        name: albumData?.translations?.ar?.name,
        name_en: albumData?.translations?.en?.name,
        //@ts-ignore
        users: albumData.users.map((user) => ({
          id: user.id,
          type: 'user'
        }))
      });
    }
  }, [albumData, reset, id]);

  return (
    <Box>
      {id != 0 &&
      (isLoading || isFetching || loadingStudents || fetchingStudents) ? (
        <SuspenseLoader />
      ) : (
        <>
          <form
            onSubmit={
              id === 0 ? handleSubmit(onSubmit) : handleSubmit(onUpdate)
            }
          >
            <div>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}
              ></Box>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    sx={inputStyle}
                    {...field}
                    label="Album Name in Arabic"
                    placeholder="Album Name in Arabic"
                    required
                    error={!!errors?.name}
                    helperText={errors?.name?.message}
                  />
                )}
              />
              <Controller
                name="name_en"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Album Name in English"
                    placeholder="Album Name in English"
                    required
                    sx={inputStyle}
                  />
                )}
              />
              {id === 0 && (
                <Box
                  sx={{
                    width: '97%',
                    marginBottom: '30px',
                    marginLeft: { sm: '16px' }
                  }}
                >
                  <FilepondUploader
                    control={control}
                    name="image"
                    type="image"
                  />
                </Box>
              )}

              <Box
                sx={{
                  width: '97%',
                  marginBottom: '30px',
                  marginLeft: { sm: '16px' }
                }}
              >
                <AddUsers users={students} control={control} errors={errors} />
              </Box>

              <Box sx={{ marginLeft: '16px' }}>
                {id === 0 ? (
                  <LoadingButton
                    loadingPosition="start"
                    variant="contained"
                    fullWidth
                    type="submit"
                    loading={createAlbum.isLoading}
                  >
                    Add Album
                  </LoadingButton>
                ) : (
                  <Stack gap={1}>
                    <LoadingButton
                      loadingPosition="start"
                      variant="contained"
                      fullWidth
                      type="submit"
                      loading={updateAlbum.isLoading}
                    >
                      Update Album
                    </LoadingButton>
                  </Stack>
                )}
              </Box>
            </div>
          </form>
        </>
      )}
    </Box>
  );
};

export default AddAlbumForm;
